import React, { useState } from 'react';
import {
  IAuditoriaClassificacaoReceitaResultado,
  IItemClassificacaoReceita,
  IContaConciliadaClassificacaoReceita,
  IContaSemReferencialClassificacaoReceita,
  IContaRedutoraClassificacaoReceita,
} from '../interfaces/IAuditoriaClassificacaoReceita';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaClassificacaoReceitaResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    case 'SEM_MOVIMENTO':
      return 'bi-dash-circle';
    case 'SEM_REFERENCIAL':
      return 'bi-question-circle';
    case 'REDUTORA_EXCLUIDA':
      return 'bi-slash-circle';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaClassificacaoReceitaDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const todasOcorrencias = resultado.classificacoesIndevidas || [];
  const todasConciliadas = resultado.contasConciliadas || [];
  const contasSemReferencial = resultado.contasSemReferencial || [];
  const contasRedutoras = resultado.contasRedutoras || [];

  const contasConciliadas = todasConciliadas.filter((x) => x.status === 'CONCILIADO');
  const contasSemMovimento = todasConciliadas.filter((x) => x.status === 'SEM_MOVIMENTO');

  const referenciaisIndevidos = todasOcorrencias.filter(
    (x) => x.tipoOcorrencia === 'REFERENCIAL_INDEVIDO_REDUTORA'
  );
  const debitosIndevidos = todasOcorrencias.filter(
    (x) => x.tipoOcorrencia !== 'REFERENCIAL_INDEVIDO_REDUTORA'
  );

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const totalOcorrencias = todasOcorrencias.length;

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Classificação de Contas de Receita — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Valor Classificado Indevidamente</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Auditoria de boa prática contábil — sem multa/imposto calculado.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Débitos diretos em conta de Receita Operacional no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      {referenciaisIndevidos.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-exclamation-triangle-fill text-warning me-2' style={{ fontSize: 16 }} />
            Referenciais RFB Indevidos em Contas Redutoras
          </h3>
          <p className='text-muted'>
            Contas de natureza redutora (que iniciam com "(-)" — IPI, ICMS ST, abatimentos, devoluções, tributos
            sobre vendas) vinculadas indevidamente a um grupo de receita positiva do Plano Referencial RFB. O referencial
            correto é o grupo de deduções correspondente (ex: 3.01.01.01.02, 3.01.01.03.02 ou 3.01.01.05.02).
          </p>
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta Redutora</th>
                  <th>Referencial Atual (Indevido)</th>
                  <th>Referencial Correto</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {referenciaisIndevidos.map((item: IItemClassificacaoReceita, idx) => {
                  const chave = `ri-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>
                          <span className='text-danger'>{item.contaReferencial || '—'}</span>
                          <div className='text-muted' style={{ fontSize: 11 }}>Receita Positiva (incorreto)</div>
                        </td>
                        <td>
                          <span className='text-success'>3.01.01.XX.02</span>
                          <div className='text-muted' style={{ fontSize: 11 }}>Grupo de Deduções do Plano RFB</div>
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fff8e1' }}>
                            <div className='py-2 px-3'>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.mensagemDiagnostico}</div>
                              <div className='mt-2'><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Débitos Indevidos em Contas de Receita</h3>
        <p className='text-muted'>
          Débitos registrados diretamente em conta de Receita Operacional — deveriam estar segregados na conta
          redutora de vendas canceladas/devoluções, para preservar a Receita Bruta íntegra (CPC 47).
        </p>

        {debitosIndevidos.length === 0 && (
          <div className='text-muted mt-4'>Nenhum débito indevido identificado no período.</div>
        )}

        {debitosIndevidos.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta Analisada</th>
                  <th>Categoria</th>
                  <th>Data</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {debitosIndevidos.map((item: IItemClassificacaoReceita, idx) => {
                  const chave = `cr-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.categoria}</td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Aviso:</strong> Classificação Indevida — débito registrado diretamente em conta de receita.</div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico || 'não identificado'}.</div>
                              <div><strong>Conta Analisada:</strong> {item.contaReferencial} - {item.descricaoConta}.</div>
                              <div>
                                <strong>Conta Recomendada:</strong>{' '}
                                {item.contaRedutoraRecomendada
                                  ? `${item.contaReferencialRedutora} - ${item.contaRedutoraRecomendada}`
                                  : 'nenhuma conta redutora cadastrada neste plano de contas'}
                                .
                              </div>
                              <div><strong>Valor:</strong> {formatCurrency(item.valor)}.</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.mensagemDiagnostico}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {contasConciliadas.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Contas de Receita Conciliadas
          </h3>
          <p className='text-muted'>
            Contas de receita com referencial RFB mapeado, com movimentação no período e sem débito indevido identificado.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Categoria</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasConciliadas.map((item: IContaConciliadaClassificacaoReceita, idx) => {
                  const chave = `crc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.categoria}</td>
                        <td>
                          <span className='auditoria-status status-CONCILIADO'>CONCILIADO</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={3} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {contasSemMovimento.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-dash-circle text-secondary me-2' style={{ fontSize: 18 }} />
            Contas de Receita Sem Movimento no Período
          </h3>
          <p className='text-muted'>
            Contas identificadas como receita (com referencial RFB mapeado) que não apresentaram nenhuma
            transação no período analisado. Presença no plano de contas confirmada — auditoria de movimento não aplicável.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Referencial RFB</th>
                  <th>Categoria</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasSemMovimento.map((item: IContaConciliadaClassificacaoReceita, idx) => {
                  const chave = `csm-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td className='text-muted' style={{ fontSize: 12 }}>{item.contaReferencial || '—'}</td>
                        <td>{item.categoria}</td>
                        <td>
                          <span className='auditoria-status' style={{ background: '#e5e7eb', color: '#374151' }}>Sem movimento</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#f9fafb' }}>
                            <div className='py-2 px-3 text-muted'>{item.mensagemDiagnostico}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {contasSemReferencial.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-question-circle text-warning me-2' style={{ fontSize: 18 }} />
            Contas Sem Referencial RFB (Análise Complementar)
          </h3>
          <p className='text-muted'>
            Contas cujo nome sugere receita mas que <strong>não possuem mapeamento I051 no ECD</strong> e não foram
            incluídas na auditoria principal. Verifique manualmente cada conta: se for receita positiva, adicione o
            referencial RFB correspondente no ECD para que a auditoria passe a considerá-la.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Código Contábil</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasSemReferencial.map((item: IContaSemReferencialClassificacaoReceita, idx) => {
                  const chave = `csr-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-aviso' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                        </td>
                        <td className='text-muted' style={{ fontSize: 12 }}>{item.contaContabil || '—'}</td>
                        <td>
                          <span className='auditoria-status' style={{ background: '#fef3c7', color: '#92400e' }}>Sem referencial RFB</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={3} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3 text-muted' style={{ fontSize: 13 }}>
                              <i className='bi bi-info-circle me-1' />
                              {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {contasRedutoras.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-slash-circle text-secondary me-2' style={{ fontSize: 18 }} />
            Contas Redutoras / Deduções de Receita
          </h3>
          <p className='text-muted'>
            Contas com referencial RFB de dedução (grupo 3.01.01.XX.02) ou nomenclatura redutora —
            corretamente <strong>excluídas</strong> da auditoria de receita positiva. São tratadas em
            validação complementar de referencial indevido (seção acima). A listagem abaixo confirma
            quais contas dedutoras foram identificadas e afastadas da regra principal.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta Redutora / Dedução</th>
                  <th>Referencial RFB (Dedução)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasRedutoras.map((item: IContaRedutoraClassificacaoReceita, idx) => {
                  const chave = `red-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td className='text-muted' style={{ fontSize: 12 }}>
                          {item.contaReferencial || '—'}
                          <div style={{ fontSize: 11 }}>Grupo de Deduções do Plano RFB</div>
                        </td>
                        <td>
                          <span className='auditoria-status' style={{ background: '#f3f4f6', color: '#6b7280' }}>
                            Excluída da regra principal
                          </span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={3} style={{ background: '#f9fafb' }}>
                            <div className='py-2 px-3 text-muted' style={{ fontSize: 13 }}>
                              <i className='bi bi-info-circle me-1' />
                              {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaClassificacaoReceitaDetalhe;
